import type { UnlockId } from '@/core/content/ids';
import { unlockStatus } from '@/core/domain/unlocks';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useServices } from '@/ui/services/ServicesContext';
import { useBack } from '@/ui/back/backStack';
import styles from './UnlockModal.module.css';
import { SpotArt } from '@/ui/art/SpotArt';

/** Unlocks big enough for the S15 modal (ux-screens §3.15); the rest only get a toast. */
export const MODAL_UNLOCKS: ReadonlySet<UnlockId> = new Set<UnlockId>([
  'unl_minimart',
  'unl_npc_orders',
  'unl_packing',
  'unl_photo_mode',
  'unl_chapter_4',
  'unl_chapter_5',
  'unl_studio',
]);

/**
 * S15 "Một góc nhỏ mới đang chờ bạn": several unlocks at once share one modal; the most important one
 * (chapter > area > feature > catalog, then content order) is the headline. Always names the next goal.
 */
export function UnlockModal({
  unlockIds,
  onClose,
  onVisit,
}: {
  unlockIds: readonly UnlockId[];
  onClose: () => void;
  onVisit: (unlockId: UnlockId) => void;
}) {
  const { t } = useI18n();
  const { content, store } = useServices();
  useBack(onClose);
  const rank = (d: { grants: readonly { type: string }[] }) =>
    d.grants.some((g) => g.type === 'chapter')
      ? 0
      : d.grants.some((g) => g.type === 'scene' || g.type === 'roomExpansion')
        ? 1
        : d.grants.some((g) => g.type === 'feature')
          ? 2
          : 3;
  const defs = unlockIds
    .map((id) => content.unlocks.get(id))
    .filter((d) => d !== undefined)
    .sort((a, b) => rank(a) - rank(b) || a.order - b.order);
  const head = defs[0];

  const lines = (() => {
    const out: string[] = [];
    for (const d of defs) {
      // The headline already names the first unlock.
      if (d !== head) out.push(t(d.nameKey));
      for (const g of d.grants) {
        if (g.type === 'chapter') {
          const ch = content.chapters.get(g.chapterId);
          if (ch) out.push(t('ui.unlock.chapter', { title: t(ch.titleKey) }));
        }
      }
    }
    return [...new Set(out)].slice(0, 3);
  })();

  // "Tiếp theo": the first locked unlock that is waiting on stars.
  const next = (() => {
    const s = store.getState();
    for (const d of [...content.unlocks.values()].sort((a, b) => a.order - b.order)) {
      const st = unlockStatus(content, s, d.id);
      if (st.kind !== 'locked') continue;
      const stars = st.missing.find((m) => m.kind === 'stars');
      if (stars?.kind === 'stars') return { name: t(d.nameKey), need: stars.need };
    }
    return null;
  })();

  if (!head) return null;
  return (
    <div className={styles.scrim}>
      <section
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="unlock-title"
        data-testid="unlock-modal"
      >
        <div className={styles.art}>
          <SpotArt name="ill_unlock" fallback="unlocks" size={150} />
        </div>
        <h2 id="unlock-title" className={styles.title}>
          {t('ui.unlock.title')}
        </h2>
        <p className={styles.opened}>{t('ui.unlock.opened', { name: t(head.nameKey) })}</p>
        <ul className={styles.lines}>
          {lines.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
        {next && <p className={styles.next}>{t('ui.unlock.next', { name: next.name, stars: next.need })}</p>}
        <div className={styles.actions}>
          <button type="button" className={styles.secondary} onClick={onClose}>
            {t('ui.unlock.later')}
          </button>
          <button
            type="button"
            className={styles.primary}
            data-testid="unlock-visit"
            onClick={() => {
              onVisit(head.id);
            }}
          >
            {t('ui.unlock.visit')}
          </button>
        </div>
      </section>
    </div>
  );
}
